/**
 * Deriving a policy's identity from its filename.
 *
 * The wizard used to make the admin pick a type from a dropdown and type a name
 * for every single policy before it would accept the file. A secondary with
 * eight policies paid for eight dropdowns and eight names, and the information
 * was already sitting in the filename they were about to upload. Bulk upload
 * removes that, which means the identity has to come from somewhere else.
 *
 * These helpers are the somewhere else. They are deliberately deterministic —
 * no AI call, no latency, no failure mode — and everything they produce is
 * editable afterwards in Settings › Policies. A misfiled policy is a label to
 * fix, not data lost: the file itself is uploaded and searchable either way.
 *
 * Shared by the wizard (display), buildAppStateFromOnboarding (slot mapping)
 * and, in spirit, policyChat's citation titles. The backend keeps its own copy
 * of `prettifyPolicyName` because it cannot import from src/ — if you change the
 * rules here, change them there too (functions/src/functions/policyChat.ts).
 */

/** Named slots on `School.policies` that a filename can be routed into. */
export type PolicySlotKind = 'behaviour' | 'safeguarding' | 'cover' | '';

/**
 * Keyword → slot. Order is significant: the first match wins, so the more
 * specific and higher-stakes patterns are listed first. "child protection"
 * outranks "behaviour" because a document called "Safeguarding and Behaviour"
 * is a safeguarding policy that mentions behaviour, not the reverse.
 */
const SLOT_PATTERNS: [PolicySlotKind, string[]][] = [
  ['safeguarding', ['safeguarding', 'child protection', 'childprotection', 'kcsie', 'keeping children safe']],
  ['behaviour', ['behaviour', 'behavior', 'anti-bullying', 'antibullying', 'bullying', 'discipline', 'code of conduct']],
  ['cover', ['cover policy', 'cover work', 'cover arrangements', 'staff absence', 'absence policy']],
];

/**
 * Which named slot does this filename belong in, if any?
 *
 * Returns '' when nothing matches — the honest answer, and the caller routes it
 * to additionalPolicies rather than guessing. Note the `cover` patterns are
 * two-word on purpose: a bare "cover" matches far too much ("Discovery",
 * "Cover Sheet"), and a wrongly-slotted cover policy displaces the real one.
 */
export function classifyPolicyFileName(fileName: string): PolicySlotKind {
  const haystack = fileName.toLowerCase().replace(/[_\-.]+/g, ' ');
  for (const [slot, needles] of SLOT_PATTERNS) {
    if (needles.some(n => haystack.includes(n))) return slot;
  }
  return '';
}

/**
 * A filename turned into something readable enough to show a cover teacher and
 * to cite in a chatbot answer.
 *
 * "behaviour-policy-2025-v3.pdf" -> "Behaviour Policy 2025 V3"
 * "Safeguarding Policy.PDF"      -> "Safeguarding Policy"   (case preserved)
 *
 * Casing rule: only re-case when the filename carries no casing information of
 * its own (all lower or all upper). A human who typed "SEND Policy for KS4"
 * knew what they meant, and title-casing it to "Send Policy For Ks4" would be
 * strictly worse — "SEND" is an acronym and "Send" is a verb.
 */
export function prettifyPolicyName(fileName: string): string {
  const withoutExt = fileName.replace(/\.[a-z0-9]{1,5}$/i, '');
  const spaced = withoutExt.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!spaced) return fileName;

  const hasOwnCasing = spaced !== spaced.toLowerCase() && spaced !== spaced.toUpperCase();
  if (hasOwnCasing) return spaced;

  return spaced
    .split(' ')
    .map(w => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
}

/**
 * The label to show for a policy, preferring anything a human set explicitly.
 * `name` may be an empty string on wizard-created records, hence the trim.
 */
export function policyDisplayName(policy: { name?: string; fileName?: string }): string {
  const explicit = policy.name?.trim();
  if (explicit) return explicit;
  if (policy.fileName) return prettifyPolicyName(policy.fileName);
  return 'School Policy';
}
